// SCR-CAT-03 Product card (frozen mockups-cat-v1): tabs Details · Prices · Barcodes · History (ADR-087).
// The Storekeeper has no Prices tab and no summary of prices (ADR-088); only the owner sees the cost (GEN-15).
// P10 (mockups-adv-v1): a bundle has Details · Components · History; a lot-tracked product swaps Barcodes for Lots
// (barcodes move into Details) so the card keeps four tabs (ADR-087).
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  EmptyState,
  List,
  ListRow,
  Notice,
  Section,
  SkeletonList,
  SummaryCard,
  Tabs,
} from '../../design/kit';
import { loadBarcodes, loadPriceHistory, type Unit } from '../../data/catalog';
import { ProductGallery } from './ProductGallery';
import { Code, Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { PURCHASING, loadProductPurchases } from '../../data/purchasing';
import { countWord } from '../stock/StockSheets';
import { useSession } from '../../app/session';
import { useCardHeader } from '../../app/Shell';
import { AddBarcodeSheet, GenerateBarcodeSheet, PriceSheet, ProductSheet } from './ProductSheets';
import { LotsTab, OptInLotsSheet, useBundleAvailability, useFamilies } from './AdvancedSheets';
import { CATALOG, useCatalogData, useMoney, useTab } from './common';

type Tab = 'details' | 'prices' | 'barcodes' | 'components' | 'lots' | 'history';
type Sheet = 'edit' | 'price' | 'barcode' | 'generate' | 'lots' | null;

export function ProductCard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const { id = '' } = useParams();
  const { role } = useSession();
  const owner = role === 'owner';
  const seesPrices = role !== 'storekeeper';
  const navigate = useNavigate();
  const money = useMoney();
  const { products, categories, brands, prices, costs } = useCatalogData();
  const product = products.data?.find((p) => p.id === id);
  const families = useFamilies();
  const availability = useBundleAvailability();
  const tabs: Tab[] = product?.is_bundle
    ? ['details', 'components', 'history']
    : product?.lot_tracked
      ? seesPrices
        ? ['details', 'prices', 'lots', 'history']
        : ['details', 'lots', 'history']
      : seesPrices
        ? ['details', 'prices', 'barcodes', 'history']
        : ['details', 'barcodes', 'history'];
  const [tab, setTab] = useTab(tabs, 'details');
  const [sheet, setSheet] = useState<Sheet>(null);
  const price = prices.data?.get(id);
  const cost = costs.data?.get(id);
  const history = useQuery({
    queryKey: [...CATALOG, 'history', id],
    queryFn: () => loadPriceHistory(id),
    enabled: seesPrices && tab === 'prices',
  });
  const barcodes = useQuery({ queryKey: [...CATALOG, 'barcodes', id], queryFn: () => loadBarcodes(id) });
  const back = useCallback(() => navigate('/products'), [navigate]);
  // PRD-13 / ADR-154: the gallery; the owner and the Storekeeper manage the photos.
  const canPhoto = role === 'owner' || role === 'storekeeper';
  useCardHeader(product ? <Name>{product.name}</Name> : t('p.products'), back);

  if (products.isLoading) return <SkeletonList label={t('loading')} />;
  if (!product) return <Notice tone="warning">{t('err.notfound')}</Notice>;
  const units: Unit[] = [
    ...(product.sell_piece ? ['piece' as const] : []),
    ...(product.sell_box ? ['box' as const] : []),
  ];
  const free = units.filter((u) => !(barcodes.data ?? []).some((b) => b.unit === u));
  const mainPrice = price ? (product.sell_piece ? price.piece_price : price.box_price) : null;
  const family = families.data?.find((f) => f.id === product.family_id);
  const bundleAt = (availability.data ?? []).filter((a) => a.bundle_id === product.id);
  const barcodeLists = units.map((u) => {
    const codes = (barcodes.data ?? []).filter((b) => b.unit === u);
    return (
      <Section key={u} title={t(`unit.${u}`)}>
        <List label={t(`unit.${u}`)}>
          {codes.length === 0 ? (
            <ListRow lead={{ icon: 'info' }} title={t('nobarcode')} />
          ) : (
            codes.map((b) => (
              <ListRow
                key={b.code}
                testId={`barcode-${b.code}`}
                lead={{ icon: 'scan' }}
                title={<Code>{b.code}</Code>}
                sub={t(`src.${b.source}`)}
              />
            ))
          )}
        </List>
      </Section>
    );
  });
  const barcodeActions = role !== 'seller' && (
    <div className="yv-chips">
      <Button pill variant="main" icon="plus" onClick={() => setSheet('barcode')}>
        {t('addbarcode')}
      </Button>
      {free.length > 0 && (
        <Button pill onClick={() => setSheet('generate')}>
          {t('generate')}
        </Button>
      )}
    </div>
  );

  return (
    <>
      {seesPrices && price && (
        <SummaryCard
          label={`${t('tab.prices')} · ${t(product.sell_piece ? 'unit.piece' : 'unit.box')}`}
          value={money(mainPrice)}
          figures={[
            ...(product.sell_piece && product.sell_box
              ? [{ label: t('unit.box'), value: money(price.box_price) }]
              : []),
            ...(product.is_bundle
              ? bundleAt
                  .filter((a) => a.kind === 'garage' || a.available > 0)
                  .map((a) => ({
                    label: a.kind === 'garage' ? t('loc.garage') : t('bun.truck'),
                    value: String(a.available),
                  }))
              : []),
            ...(owner
              ? [
                  {
                    label: t('avgcost'),
                    value: cost?.avg_cost !== null && cost?.avg_cost !== undefined ? money(cost.avg_cost) : '—',
                  },
                ]
              : []),
          ]}
        />
      )}
      <Tabs
        label={t('c3.title')}
        value={tab}
        onChange={(v) => setTab(v as Tab)}
        items={tabs.map((v) => ({ id: v, label: t(`tab.${v}`) }))}
      />

      {tab === 'details' && (
        <>
          <ProductGallery
            product={product.id}
            name={product.name}
            caption={mainPrice !== null && seesPrices ? `${product.name} — ${money(mainPrice)}` : product.name}
            canEdit={canPhoto}
          />
          <List label={t('tab.details')}>
            {family && (
              <>
                <ListRow lead={{ icon: 'layers' }} title={t('f.family')} sub={<Name>{family.name}</Name>} />
                {family.attributes.map((a, i) => (
                  <ListRow
                    key={a.position}
                    lead={{ icon: 'tag' }}
                    title={<Name>{a.name}</Name>}
                    sub={<Name>{product.attr_values?.[i] ?? '—'}</Name>}
                  />
                ))}
              </>
            )}
            <ListRow lead={{ icon: 'tag' }} title={t('sku')} value={product.sku ? <Code>{product.sku}</Code> : '—'} />
            <ListRow
              lead={{ icon: 'folder' }}
              title={t('category')}
              sub={product.category_path ? <Name>{product.category_path}</Name> : '—'}
            />
            <ListRow
              lead={{ icon: 'tag' }}
              title={t('brand')}
              sub={product.brand ? <Name>{product.brand}</Name> : '—'}
            />
            <ListRow
              lead={{ icon: 'box' }}
              title={t('units')}
              value={[product.sell_piece && t('unit.piece'), product.sell_box && t('unit.box')]
                .filter(Boolean)
                .join(' · ')}
              sub={product.box_capacity ? t('boxof', { n: product.box_capacity }) : t('loose')}
            />
            <ListRow lead={{ icon: 'alert' }} title={t('reorder')} value={String(product.reorder_level)} />
            {product.notes && <ListRow lead={{ icon: 'file' }} title={t('notes')} sub={<Name>{product.notes}</Name>} />}
            <ListRow
              lead={{ icon: 'check' }}
              title={t('status')}
              badge={product.active ? <Badge tone="success">{t('active')}</Badge> : <Badge>{t('inactive')}</Badge>}
            />
          </List>
          {owner && (
            <div className="yv-chips">
              <Button pill onClick={() => setSheet('edit')}>
                {t('edit.btn')}
              </Button>
              {!product.is_bundle && !product.lot_tracked && (
                <Button pill onClick={() => setSheet('lots')}>
                  {t('lot.track')}
                </Button>
              )}
            </div>
          )}
          {product.lot_tracked && (
            <>
              {barcodeLists}
              {barcodeActions}
            </>
          )}
        </>
      )}

      {tab === 'components' && product.is_bundle && (
        <>
          <List label={t('tab.components')}>
            {(product.components ?? []).map((c) => (
              <ListRow
                key={c.product_id}
                testId={`component-${c.sku ?? c.product_id}`}
                lead={{ icon: 'tag' }}
                title={<Name>{c.name}</Name>}
                sub={
                  <>
                    {c.sku && (
                      <>
                        <Code>{c.sku}</Code> ·{' '}
                      </>
                    )}
                    {t('bun.per')}: {c.quantity}
                  </>
                }
                onOpen={() => navigate(`/products/${c.product_id}`)}
              />
            ))}
          </List>
          <Notice tone="info">{t('bun.nostock')}</Notice>
        </>
      )}

      {tab === 'lots' && product.lot_tracked && <LotsTab product={product} />}

      {tab === 'prices' && seesPrices && (
        <>
          {owner && (
            <div className="yv-chips">
              <Button pill variant="main" onClick={() => setSheet('price')}>
                {t('changeprice')}
              </Button>
            </div>
          )}
          {owner && <ProductPurchases product={product.id} />}
          <Section title={t('pricehistory')}>
            {!history.data ? (
              <SkeletonList label={t('loading')} />
            ) : (
              <List label={t('pricehistory')}>
                {history.data.map((h) => (
                  <ListRow
                    key={h.id}
                    lead={{ icon: 'clock' }}
                    title={
                      h.old_price === null
                        ? `${t('h1')} · ${t(`unit.${h.unit}`)} · ${money(h.new_price)}`
                        : `${t(`unit.${h.unit}`)} · ${money(h.old_price)} → ${money(h.new_price)}`
                    }
                    sub={
                      <>
                        {formatDateTime(h.changed_at, lang)}
                        {h.reason && (
                          <>
                            {' · '}
                            <Name>{h.reason}</Name>
                          </>
                        )}
                        {h.changed_by_name && (
                          <>
                            {' · '}
                            <Name>{h.changed_by_name}</Name>
                          </>
                        )}
                      </>
                    }
                  />
                ))}
              </List>
            )}
          </Section>
        </>
      )}

      {tab === 'barcodes' && (
        <>
          {barcodeLists}
          {barcodeActions}
        </>
      )}

      {tab === 'history' && <EmptyState icon="layers" title={t('history.empty')} text={t('history.empty.text')} />}

      {sheet === 'edit' && (
        <ProductSheet
          product={product}
          price={price}
          categories={categories.data ?? []}
          brands={brands.data ?? []}
          onClose={() => setSheet(null)}
          onSaved={(saved) => {
            setSheet(null);
            if (!saved) navigate('/products');
          }}
        />
      )}
      {sheet === 'price' && (
        <PriceSheet product={product} price={price} onClose={() => setSheet(null)} onSaved={() => setSheet(null)} />
      )}
      {sheet === 'barcode' && <AddBarcodeSheet product={product} onClose={() => setSheet(null)} />}
      {sheet === 'generate' && <GenerateBarcodeSheet product={product} units={free} onClose={() => setSheet(null)} />}
      {sheet === 'lots' && <OptInLotsSheet product={product} onClose={() => setSheet(null)} />}
    </>
  );
}

/** SCR-PUR-08 (owner): average cost, last purchase price per piece and the last 5 purchases (PRD-11, GEN-09). */
function ProductPurchases({ product }: { product: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const { costs } = useCatalogData();
  const cost = costs.data?.get(product);
  const purchases = useQuery({
    queryKey: [...PURCHASING, 'product', product],
    queryFn: () => loadProductPurchases(product),
  });
  const avg = cost?.avg_cost ?? null;
  const last = cost?.last_purchase_price ?? null;
  const money = (v: number | string) => formatMoney(v, lang);
  return (
    <Section title={t('pc.title')}>
      <List label={t('pc.title')}>
        <ListRow
          lead={{ icon: 'chart' }}
          title={t('pc.avg')}
          value={avg !== null ? `${money(avg)} ${t('perpiece')}` : '—'}
        />
        <ListRow
          lead={{ icon: 'cart' }}
          testId="last-purchase"
          title={t('pc.last')}
          value={last !== null ? `${money(last)} ${t('perpiece')}` : '—'}
          sub={
            last !== null && avg !== null && Number(last) > Number(avg) ? (
              <Badge tone="warning">{t('pc.above')}</Badge>
            ) : undefined
          }
        />
      </List>
      {purchases.data && purchases.data.length > 0 && (
        <List label={t('pc.last5')}>
          {purchases.data.map((p) => (
            <ListRow
              key={`${p.number}-${p.unit}`}
              title={
                <>
                  {formatDate(p.business_date, lang)} · <Name>{p.supplier}</Name>
                </>
              }
              sub={`${countWord(t, p.unit, p.quantity)} × ${money(p.unit_price)}`}
              value={`${money(p.price_per_piece)} ${t('perpiece')}`}
            />
          ))}
        </List>
      )}
    </Section>
  );
}
